import React from "react";
import {
  Alert,
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import { Dialog } from "../../../components/Dialogs/Dialog";
import { useDispatchWrapper } from "../../../hooks";
import {
  enrollRecordsAsync,
  fetchActiveSequencesByModuleAsync,
} from "../../../redux/emailSequences/emailSequencesActions";
import {
  IEnrolResult,
  ISequenceListRow,
} from "../../../redux/emailSequences/emailSequences.types";
import {
  ENROL_NOT_CREATED_LABELS,
  SequenceModule,
} from "../emailSequences.labels";

interface IAddToSequenceDialogProps {
  open: boolean;
  module: SequenceModule;
  /** Record ids to enrol (customer_policy_id / customer_id / lead_id). */
  moduleCodes: (string | number)[];
  onClose: () => void;
  onEnrolled?: () => void;
}

export const AddToSequenceDialog: React.FC<IAddToSequenceDialogProps> = ({
  open,
  module,
  moduleCodes,
  onClose,
  onEnrolled,
}) => {
  const dispatch = useDispatchWrapper();
  const [sequences, setSequences] = React.useState<ISequenceListRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState<string>("");
  const [results, setResults] = React.useState<IEnrolResult[] | null>(null);
  const [showSkipped, setShowSkipped] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setLoading(true);
      setResults(null);
      setSelected("");
      dispatch(
        fetchActiveSequencesByModuleAsync(module, (list) => {
          setSequences(list);
          setLoading(false);
        })
      );
    }
  }, [open, module, dispatch]);

  const handleEnrol = () => {
    if (!selected) return;
    dispatch(
      enrollRecordsAsync(
        {
          sequence_code: selected,
          module_name: module,
          module_codes: moduleCodes,
        },
        (ok, res) => {
          if (ok && res) {
            setResults(res);
            onEnrolled?.();
          }
        }
      )
    );
  };

  const createdCount = results?.filter((r) => r.created).length || 0;
  const skipped = results?.filter((r) => !r.created) || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add to sequence"
      size="sm"
      fullWidth
      actions={
        results
          ? [
              {
                type: "button",
                variant: "contained",
                label: "Done",
                onClick: onClose,
              },
            ]
          : [
              {
                type: "button",
                variant: "text",
                label: "Cancel",
                onClick: onClose,
              },
              {
                type: "button",
                variant: "contained",
                label: `Enrol (${moduleCodes.length})`,
                disabled: !selected,
                onClick: handleEnrol,
              },
            ]
      }
    >
      {results ? (
        <Box>
          <Alert severity="success" sx={{ mb: 1 }}>
            {createdCount} enrolled, {skipped.length} skipped
          </Alert>
          {skipped.length > 0 && (
            <>
              <ListItemButton onClick={() => setShowSkipped((s) => !s)}>
                <ListItemText primary={`Skipped records (${skipped.length})`} />
                {showSkipped ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={showSkipped}>
                <List dense>
                  {skipped.map((s) => (
                    <ListItem key={String(s.module_code)}>
                      <ListItemText
                        primary={String(s.module_code)}
                        secondary={
                          s.reason
                            ? ENROL_NOT_CREATED_LABELS[s.reason] || s.reason
                            : "Skipped"
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Enrolling {moduleCodes.length} {module.toLowerCase()} record(s).
          </Typography>
          {loading ? (
            <Typography>Loading sequences…</Typography>
          ) : sequences.length === 0 ? (
            <Alert severity="info">
              No active sequences for this module.
            </Alert>
          ) : (
            <List>
              {sequences.map((seq) => {
                const stepSummary =
                  (seq as any).steps?.length !== undefined
                    ? `${(seq as any).steps.length} steps`
                    : "";
                return (
                  <ListItemButton
                    key={seq.sequence_code}
                    onClick={() => setSelected(seq.sequence_code)}
                  >
                    <Radio checked={selected === seq.sequence_code} />
                    <ListItemText
                      primary={seq.sequence_name}
                      secondary={stepSummary}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      )}
    </Dialog>
  );
};
